// Task alarm sounds (Web Audio synthesis — no binary assets) + vibration helper.

export type AlarmSoundKey = 'bell' | 'double_beep' | 'xylophone' | 'rising' | 'marimba';

export const ALARM_SOUNDS: { key: AlarmSoundKey; i18nKey: string }[] = [
  { key: 'bell', i18nKey: 'shiftTemplates.alarmSoundBell' },
  { key: 'double_beep', i18nKey: 'shiftTemplates.alarmSoundDoubleBeep' },
  { key: 'xylophone', i18nKey: 'shiftTemplates.alarmSoundXylophone' },
  { key: 'rising', i18nKey: 'shiftTemplates.alarmSoundRising' },
  { key: 'marimba', i18nKey: 'shiftTemplates.alarmSoundMarimba' },
];

type Note = { freq: number; at: number; dur: number; type?: OscillatorType; gain?: number };

const PATTERNS: Record<AlarmSoundKey, Note[]> = {
  bell: [
    { freq: 880, at: 0, dur: 1.2, type: 'sine', gain: 0.5 },
    { freq: 1320, at: 0, dur: 0.9, type: 'sine', gain: 0.2 },
    { freq: 880, at: 0.65, dur: 1.2, type: 'sine', gain: 0.35 },
  ],
  double_beep: [
    { freq: 1000, at: 0, dur: 0.16, type: 'square', gain: 0.25 },
    { freq: 1000, at: 0.24, dur: 0.16, type: 'square', gain: 0.25 },
  ],
  xylophone: [
    { freq: 1046, at: 0, dur: 0.28, type: 'triangle', gain: 0.4 },
    { freq: 1318, at: 0.16, dur: 0.28, type: 'triangle', gain: 0.4 },
    { freq: 1568, at: 0.32, dur: 0.4, type: 'triangle', gain: 0.4 },
  ],
  rising: [
    { freq: 523, at: 0, dur: 0.18, type: 'sine', gain: 0.35 },
    { freq: 659, at: 0.14, dur: 0.18, type: 'sine', gain: 0.35 },
    { freq: 784, at: 0.28, dur: 0.18, type: 'sine', gain: 0.35 },
    { freq: 1046, at: 0.42, dur: 0.5, type: 'sine', gain: 0.35 },
  ],
  marimba: [
    { freq: 587, at: 0, dur: 0.35, type: 'sine', gain: 0.45 },
    { freq: 880, at: 0.18, dur: 0.35, type: 'sine', gain: 0.4 },
    { freq: 587, at: 0.5, dur: 0.45, type: 'sine', gain: 0.3 },
  ],
};

let ctx: AudioContext | null = null;

function getContext(): AudioContext | null {
  try {
    const Ctor = window.AudioContext || (window as any).webkitAudioContext;
    if (!Ctor) return null;
    if (!ctx) ctx = new Ctor();
    if (ctx.state === 'suspended') void ctx.resume();
    return ctx;
  } catch {
    return null;
  }
}

/** Should be called from a user gesture so mobile browsers allow later playback. */
export function primeAlarmAudio() {
  getContext();
}

export function playAlarmSound(sound: AlarmSoundKey = 'bell') {
  const audio = getContext();
  if (!audio) return;

  const pattern = PATTERNS[sound] ?? PATTERNS.bell;
  const start = audio.currentTime + 0.02;

  pattern.forEach((note) => {
    const osc = audio.createOscillator();
    const gain = audio.createGain();
    osc.type = note.type ?? 'sine';
    osc.frequency.value = note.freq;

    const t0 = start + note.at;
    const peak = note.gain ?? 0.3;
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(peak, t0 + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + note.dur);

    osc.connect(gain).connect(audio.destination);
    osc.start(t0);
    osc.stop(t0 + note.dur + 0.05);
  });
}

export function vibrateAlarm() {
  try {
    if ('vibrate' in navigator) {
      navigator.vibrate([300, 150, 300]);
    }
  } catch {
    /* ignore */
  }
}

export function triggerAlarm(sound: AlarmSoundKey, vibrate: boolean) {
  playAlarmSound(sound);
  if (vibrate) vibrateAlarm();
}
